/**
 * @file About.tsx
 * @description About page describing Ultimind's founders, mission, and background.
 */

/**
 * AboutPage component introduces the founding team and company mission.
 */
export default function AboutPage() {
  return (
    <div className="bg-slate-950">
      <section className="mx-auto max-w-5xl px-4 pb-16 pt-10 md:px-6 md:pt-14">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 md:text-4xl">
          About Ultimind
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 md:text-base">
          Ultimind is a Singapore-headquartered technology company with a
          research and development center in Hangzhou, China. We are dedicated
          to making intelligent systems usable in the real world, combining
          cutting-edge Agentic AI research with deep experience in enterprise
          software.
        </p>

        <section className="mt-10 grid gap-8 md:grid-cols-3">
          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="text-sm font-semibold text-sky-300 md:text-base">
              Founding story
            </h2>
            <p className="mt-3 text-xs text-slate-300 md:text-sm">
              Ultimind was co-founded by Dr. Fabian from the Institute of
              Computing Innovation at Zhejiang University together with Dennis
              and Steve in Singapore. The team brings together academic
              expertise, product leadership, and enterprise operations.
            </p>
          </article>
          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="text-sm font-semibold text-sky-300 md:text-base">
              Mission
            </h2>
            <p className="mt-3 text-xs text-slate-300 md:text-sm">
              Our mission is to unlock the next wave of productivity by turning
              every enterprise process into a collaborative network of AI
              agents—augmenting people, not replacing them.
            </p>
          </article>
          <article className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="text-sm font-semibold text-sky-300 md:text-base">
              R&amp;D advantage
            </h2>
            <p className="mt-3 text-xs text-slate-300 md:text-sm">
              Close collaboration with leading research institutes enables us to
              rapidly translate advances in multi-agent reasoning, retrieval
              augmented generation, and tool-use into robust products.
            </p>
          </article>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-semibold text-slate-50 md:text-2xl">
            Leadership team
          </h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h3 className="text-sm font-semibold text-slate-100 md:text-base">
                Dr. Fabian
              </h3>
              <p className="mt-1 text-xs text-sky-300 md:text-sm">
                Co-founder · Chief Scientist
              </p>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                Leads Ultimind&apos;s research agenda on multi-agent
                architectures, knowledge graphs, and AI governance, drawing on
                his academic background at Zhejiang University.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h3 className="text-sm font-semibold text-slate-100 md:text-base">
                Dennis
              </h3>
              <p className="mt-1 text-xs text-sky-300 md:text-sm">
                Co-founder · CEO
              </p>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                Oversees strategy, partnerships, and go-to-market, helping
                enterprises design high-impact Agentic AI roadmaps.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h3 className="text-sm font-semibold text-slate-100 md:text-base">
                Steve
              </h3>
              <p className="mt-1 text-xs text-sky-300 md:text-sm">
                Co-founder · COO
              </p>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                Focuses on delivery excellence and large-scale deployment,
                ensuring Ultimind solutions integrate smoothly into complex
                enterprise environments.
              </p>
            </div>
          </div>
        </section>
      </section>
    </div>
  )
}
